import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { confirmRegistration } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import { navigateToBranded } from "@/lib/navigateToBranded";
import { getUtms } from "@/lib/getUtms";
import { PASSWORD_RULES } from "@/lib/passwordRules";
import StepHeader from "@/components/StepHeader";
import PasswordField from "@/components/PasswordField";
import ErrorModal from "@/components/ErrorModal";
import { extractApiError } from "@/lib/extractApiError";

const RegisterSetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const registerData = sessionStorage.getItem("registerData");
  const tokenParam = searchParams.get("token");

  // Page guard
  useEffect(() => {
    if (!registerData && !tokenParam) navigate("/register");
  }, [registerData, tokenParam, navigate]);

  const allPassed = PASSWORD_RULES.every((r) => r.test(password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allPassed) return;
    setError(null);
    setIsLoading(true);

    const parsed = registerData ? JSON.parse(registerData) : null;
    const token = parsed?.token?.uuid || tokenParam;
    const UTMs = getUtms();

    try {
      const result = await confirmRegistration(
        {
          consumer: { password },
          campaignId: sessionStorage.getItem("ls") || null,
          utm: UTMs?.utm_campaign || null,
        },
        token
      );
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "sign_up",
          user_account_source: sessionStorage.getItem("ls") || UTMs?.utm_campaign || "direct",
        });
      }
      sessionStorage.removeItem("registerData");
      navigateToBranded(result.data.token.uuid);
    } catch (err) {
      const apiError = err.response?.data;
      if (apiError?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(apiError.data.details, navigate);
      } else {
        setError(extractApiError(apiError));
      }
      setIsLoading(false);
    }
  };

  if (!registerData && !tokenParam) return null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        className="absolute top-3 left-3 z-10 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
        onClick={() => { sessionStorage.removeItem("migration"); navigate("/"); }}
      >
        Anmelden
      </Button>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
      >
        <div className="flex flex-col gap-4 items-center w-full">
          <StepHeader stepTitle="Fast geschafft!" step={5} migration={sessionStorage.getItem("migration") === "true"} />
          <p className="text-black text-center text-[18px] font-medium leading-[1.4]">
            Richte dein Passwort ein.
          </p>
        </div>

        <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <ErrorModal
            open={!!error}
            onClose={() => setError(null)}
            message={error?.message}
            errorCode={error?.errorCode}
          />

          <Button
            type="submit"
            className="w-full md:h-[54px] md:text-[24px] md:rounded-[14px]"
            size="sm"
            variant={allPassed ? "default" : "disabled"}
            disabled={!allPassed || isLoading}
          >
            {isLoading ? "Bitte warte..." : "Weiter"}
          </Button>
        </div>
      </form>
    </>
  );
};

export default RegisterSetPassword;
